import React, { useState } from 'react';
import paypalImg from '../../assets/paypal.jpg';
import './styles/online.css';
import './styles/support-info.css';

/**
 * OnlineRules - Pantalla de normas y requisitos antes de entrar al lobby online
 * 
 * Muestra las reglas del juego online, requisitos de cámara y código de conducta.
 * El jugador debe aceptar las normas antes de poder entrar a la sala.
 * Cualquier jugador autenticado puede entrar; las normas dejan claro que el
 * acceso puede revocarse si hay mal comportamiento.
 * 
 * La cámara se gestiona directamente en la app (WebRTC P2P).
 */
const OnlineRules = ({ onBack, onEnterLobby, onSupportInfo }) => {
  const [accepted, setAccepted] = useState(() => {
    // Si ya aceptó antes, no volver a pedir
    return localStorage.getItem('501-online-rules-accepted') === 'true';
  });

  const handleEnter = () => {
    localStorage.setItem('501-online-rules-accepted', 'true');
    onEnterLobby();
  };

  return (
    <div className="dart-league-dashboard training-menu-container">

        {/* Header */}
        <header className="training-menu-header online-rules-header">
          <div className="training-header-top">
            <button className="training-back-btn" onClick={onBack}>
              ← Volver
            </button>
          </div>
          <div className="training-header-icon">
            <img src="/Logo26.png" alt="501 Dart League" className="training-header-logo" />
          </div>
          <h1 className="training-menu-title">Juego Online</h1>
          <p className="training-menu-subtitle">
            Lee las normas para una experiencia justa y profesional.
          </p>
        </header>

        {/* Tip de cámara destacado (ancho completo) */}
        <div className="online-rules-highlight" style={{ marginBottom: '24px' }}>
          <span className="highlight-icon">📹</span>
          <p>
            <strong>Cámara obligatoria:</strong> Antes de jugar, comprueba que tu webcam 
            apunta bien a la diana y que el navegador tiene permisos de cámara y audio.
            <br />
            <strong>Importante:</strong> al entrar en la sala online, pulsa <strong>🎥 Configurar cámara</strong>, guárdala y luego ya podrás invitar jugadores fácilmente.
          </p>
        </div>

        {/* Grid 2x2 de secciones */}
        <div className="online-rules-grid">

        {/* 1. Cámara y Posición */}
        <section className="online-rules-section camera">
          <div className="online-rules-section-header">
            <div className="online-rules-section-icon">📹</div>
            <h2>Cámara y Posición</h2>
          </div>
          <ul className="online-rules-list">
            <li>
              <span className="rule-icon">✅</span>
              <span>Recomendamos usar una webcam USB dedicada. Necesitas el dispositivo libre para apuntar las puntuaciones durante la partida.</span>
            </li>
            <li>
              <span className="rule-icon">✅</span>
              <span>Sitúa la cámara a un lado de la diana, a una distancia segura, con la diana completa visible y bien iluminada.</span>
            </li>
            <li>
              <span className="rule-icon">✅</span>
              <span>Un trípode pequeño o cualquier soporte fijo vale. Lo importante es que no se mueva durante la partida.</span>
            </li>
            <li>
              <span className="rule-icon">✅</span>
              <span>Configura la cámara una vez en el lobby. Esa configuración se guarda para las siguientes partidas.</span>
            </li>
            <li>
              <span className="rule-icon">✅</span>
              <span>Si la conexión de vídeo se corta, hay 60 segundos para recuperarla. Pasado ese tiempo, se pierde la partida.</span>
            </li>
            <li>
              <span className="rule-icon">🔒</span>
              <span>Tu vídeo se transmite en directo solo a tu rival (conexión P2P). No se graba ni se almacena en ningún servidor.</span>
            </li>
          </ul>
        </section>

        {/* 2. Fair Play */}
        <section className="online-rules-section fair-play">
          <div className="online-rules-section-header">
            <div className="online-rules-section-icon">🤝</div>
            <h2>Fair Play y Honestidad</h2>
          </div>
          <ul className="online-rules-list">
            <li>
              <span className="rule-icon">🎯</span>
              <span>Marca la puntuación real. El rival lo verifica por cámara en directo.</span>
            </li>
            <li>
              <span className="rule-icon">🎯</span>
              <span>Ante dudas, comunicación de buena fe. Si no hay acuerdo, se repite la tirada.</span>
            </li>
            <li>
              <span className="rule-icon">🎯</span>
              <span>Cierre con doble obligatorio. Si no está claro en el doble, no cuenta.</span>
            </li>
            <li>
              <span className="rule-icon">🎯</span>
              <span>No extraer los dardos hasta que ambos confirmen la puntuación.</span>
            </li>
          </ul>
        </section>

        {/* 3. Desarrollo de la Partida */}
        <section className="online-rules-section match">
          <div className="online-rules-section-header">
            <div className="online-rules-section-icon">🏁</div>
            <h2>Desarrollo de la Partida</h2>
          </div>
          <ul className="online-rules-list">
            <li>
              <span className="rule-icon">⚡</span>
              <span>Máximo 40 segundos por ronda de 3 dardos.</span>
            </li>
            <li>
              <span className="rule-icon">⚡</span>
              <span>El retado elige quién tira primero. Se alterna por leg.</span>
            </li>
            <li>
              <span className="rule-icon">⚡</span>
              <span>Desconexión: 90 segundos para reconectar o se pierde la partida.</span>
            </li>
            <li>
              <span className="rule-icon">⚡</span>
              <span>Puedes abandonar, pero se registra como derrota.</span>
            </li>
          </ul>
        </section>

        {/* 4. Código de Conducta */}
        <section className="online-rules-section conduct">
          <div className="online-rules-section-header">
            <div className="online-rules-section-icon">⚖️</div>
            <h2>Código de Conducta</h2>
          </div>
          <ul className="online-rules-list">
            <li>
              <span className="rule-icon">🚫</span>
              <span>Cero tolerancia con insultos o comportamiento antideportivo.</span>
            </li>
            <li>
              <span className="rule-icon">🚫</span>
              <span>Prohibido falsear puntuaciones. Se puede reportar al jugador.</span>
            </li>
            <li>
              <span className="rule-icon">🚫</span>
              <span>Respeta al rival: saluda, felicita y acepta el resultado.</span>
            </li>
            <li>
              <span className="rule-icon">🚫</span>
              <span>Incumplir las normas conlleva suspensión temporal o permanente.</span>
            </li>
          </ul>
        </section>

        </div>

        {/* Banner discreto de apoyo */}
        <div className="support-banner" onClick={onSupportInfo} role="button" tabIndex={0}>
          <span className="support-banner-icon">❤️</span>
          <div className="support-banner-text">
            <p>
              <strong>Apoya el proyecto</strong><br />
              501 Dart League es gratis y sin anuncios. Si te gusta, ayúdanos a seguir mejorando.
            </p>
          </div>
          <img src={paypalImg} alt="PayPal" className="support-banner-paypal" />
          <span className="support-banner-arrow">›</span>
        </div>

        {/* Entrada abierta con moderación posterior */}
        <div className="online-rules-enter">
          {!accepted && (
            <label className="online-rules-accept">
              <input 
                type="checkbox" 
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <span>He leído y acepto las normas del juego online</span>
            </label>
          )}

          <button 
            className="online-rules-enter-btn"
            disabled={!accepted}
            onClick={handleEnter}
            style={{ opacity: accepted ? 1 : 0.5, cursor: accepted ? 'pointer' : 'not-allowed' }}
          >
            🚀 Entrar a la Sala Online
          </button>

          <p className="online-rules-disclaimer">
            La sala está abierta a todos los jugadores autenticados. Tu nombre será visible para otros jugadores.
            Juega, disfruta y ayúdanos a mantener un ambiente de respeto para todos.
          </p>
        </div>

    </div>
  );
};

export default OnlineRules;
